import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { EMPTY, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from './services/toast.service';

@Injectable()
export class ProgressInterceptor implements HttpInterceptor {
	private router = inject(Router);
	private inj = inject(Injector);
	private toast = inject(ToastService);

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			catchError((error: unknown) => {
				if (req.headers.has('x-ignore-errors')) {
					// some errors we want to allow to pass through this interceptor
					if (req.headers.get('x-ignore-errors') === 'pass-through') {
						return throwError(() => error);
					}
					return EMPTY;
				}
				// Consider using the x header request above ^ to bypass the progress interceptor
				if (error instanceof HttpErrorResponse) {
					const exeptedUrls = [
						'parent/add_student',
						'onboard/schools/check_school',
						'discovery/find',
						'discovery/email_info',
						'sessions',
						'pass_requests',
						'hall_passes/bulk_create',
						'hall_passes',
						'//server.test-cors.org',
						'attendee/add',
						'DeleteSchedule',
						'DeleteScheduleGroupWithDependencies',
						'duplicate_progress',
						// Publishing a schedule (UpdateSchoolYear) shows its own specific error via
						// schedules-editor.component.ts's onConfirmPublishClick, same as duplicate_progress above.
						'UpdateSchoolYear',
					].every((_url) => typeof error.url === 'string' && error.url.search(_url) < 0);

					// Init 401s are explained elsewhere: by the session-expired toast, or on the login problem page.
					const isInitUnauthorized = error.status === 401 && typeof error.url === 'string' && error.url.includes('v2/users/Init');

					if (error.status >= 400 && error.status !== 403 && error.status < 600 && exeptedUrls && !isInitUnauthorized) {
						this.toast.openToast(
							{
								title: 'Oh no! Something went wrong',
								subtitle: `Please try refreshing the page. If the issue keeps occurring, contact us at support@smartpass.app. (${error.status})`,
								type: 'error',
							},
							String(error.status)
						);
					}
				}
				return throwError(() => error);
			})
		);
	}
}
